import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'
import { FC, ReactNode, createContext, useContext, useCallback, useState, useEffect, useLayoutEffect } from 'react'
import { BrowserClient } from './ampli'
import React from 'react'
import { events } from './engagement/events'

const { GATSBY_AMPLITUDE_EXPERIMENT_CLIENT_ID } = process.env

export type ExperimentContext = { client: ExperimentClient | undefined }

export const AmplitudeExperimentContext = createContext<ExperimentContext>({
  client: undefined,
})

export type VariantInfo = {
  enabled: true
  value: 'control' | 'treatment'
} | {
  enabled: false
  value: undefined
}

const IS_SERVER = typeof window === 'undefined'

/**
 * Get the value of an experiment or feature flag
 * Configure experiments in Amplitude Experiment dashboard
 *
 * value is undefined until AE loads user data
 */
export const useVariant = (flag: string): VariantInfo => {
  const { client: experimentClient } = useContext(AmplitudeExperimentContext)

  const variant = experimentClient?.variant(flag)

  const value = String(variant?.value)

  const result = Object.freeze({
    enabled: /^(yes|on|true|treatment)$/i.test(value),
    value: value === undefined ? 'control' : value
  }) as VariantInfo

  return result
}

export const AmplitudeExperimentProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [experimentClient, setExperimentClient] = useState<ExperimentClient | undefined>(undefined)

  if (IS_SERVER) {
    return <>{children}</>
  }

  const amplitudeExperiment = Experiment.initializeWithAmplitudeAnalytics(
    GATSBY_AMPLITUDE_EXPERIMENT_CLIENT_ID!
  )

  const onAmplitudeReady = useCallback((client: BrowserClient) => {
    console.log('Amplitude Experiment Client ready')

    async function fetchExperiment() {
      const identity = { device_id: client.getDeviceId() }
      const experimentClient = await amplitudeExperiment.fetch(identity)
      setExperimentClient(experimentClient)
    }

    fetchExperiment().catch((error) => {
      console.error('Error fetching experiment:', error)
    })
  }, [experimentClient])

  useLayoutEffect(() => {
    events.once('ready', onAmplitudeReady)
  })

  if (!experimentClient) {
    return <div>Loading AmplitudeExperimentContext...</div>
  }

  return (
    <AmplitudeExperimentContext.Provider value={{ client: experimentClient }}>
      {children}
    </AmplitudeExperimentContext.Provider>
  )
}
